/**
 * Mock dataset data for StarNexus platform
 * @module data/starnexus/mockDatasets
 */

import type {
  Dataset,
  DatasetStatus,
  ProcessingType,
} from '../../types/starnexus';

// Helper to create dates relative to now
const daysAgo = (days: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

const hoursAgo = (hours: number): Date => {
  const date = new Date();
  date.setHours(date.getHours() - hours);
  return date;
};

// Mock datasets with realistic data
export const mockDatasets: Dataset[] = [
  {
    datasetId: 'ds-001',
    datasetName: 'sales-transactions-2025',
    description: 'Daily sales transaction data from all retail channels',
    teamId: 'team-001',
    teamName: 'Retail Analytics',
    status: 'active',
    processingType: 'csv-to-json',
    config: {
      basicSettings: {
        datasetName: 'sales-transactions-2025',
        teamId: 'team-001',
        description: 'Daily sales transaction data from all retail channels',
        processingType: 'csv-to-json',
      },
      schemaConfig: [
        {
          columnName: 'transaction_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['TXN-001', 'TXN-002'],
        },
        {
          columnName: 'amount',
          dataType: 'decimal',
          nullable: false,
          sampleValues: ['99.99', '149.50'],
        },
        {
          columnName: 'timestamp',
          dataType: 'timestamp',
          nullable: false,
          sampleValues: ['2025-01-15T10:30:00Z'],
        },
        {
          columnName: 'customer_id',
          dataType: 'string',
          nullable: true,
          sampleValues: ['CUST-123', 'CUST-456'],
        },
      ],
      sqlTransform:
        'SELECT transaction_id, amount, timestamp, customer_id FROM source WHERE amount > 0',
      processingOptions: {
        concurrencyLimit: 10,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: true,
          checkDuplicates: true,
        },
      },
      andesSettings: {
        provider: 'retail-data-provider',
        tableName: 'sales_transactions',
        tableVersion: 1,
        partitionKeys: ['date', 'region'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 1250,
      successRate: 98.5,
      avgProcessingTime: 45,
      lastActivity: hoursAgo(2),
      trend: { filesProcessed: 125, change: 12, changeType: 'positive' },
    },
    createdBy: {
      userId: 'user-001',
      name: 'Alice Chen',
      email: 'alice@example.com',
    },
    createdAt: daysAgo(90),
    updatedAt: hoursAgo(2),
  },
  {
    datasetId: 'ds-002',
    datasetName: 'inventory-updates',
    description: 'Real-time inventory level updates from warehouses',
    teamId: 'team-001',
    teamName: 'Retail Analytics',
    status: 'active',
    processingType: 'json-passthrough',
    config: {
      basicSettings: {
        datasetName: 'inventory-updates',
        teamId: 'team-001',
        description: 'Real-time inventory level updates from warehouses',
        processingType: 'json-passthrough',
      },
      schemaConfig: [
        {
          columnName: 'sku',
          dataType: 'string',
          nullable: false,
          sampleValues: ['SKU-12345', 'SKU-67890'],
        },
        {
          columnName: 'quantity',
          dataType: 'integer',
          nullable: false,
          sampleValues: ['100', '250'],
        },
        {
          columnName: 'warehouse_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['WH-SEA', 'WH-NYC'],
        },
      ],
      sqlTransform: 'SELECT * FROM source',
      processingOptions: {
        concurrencyLimit: 15,
        retryPolicy: { maxAttempts: 5, backoffMultiplier: 1.5 },
        validation: {
          checkSchema: true,
          checkSize: false,
          checkDuplicates: false,
        },
      },
      andesSettings: {
        provider: 'inventory-provider',
        tableName: 'inventory_levels',
        tableVersion: 2,
        partitionKeys: ['warehouse_id'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 8500,
      successRate: 99.2,
      avgProcessingTime: 12,
      lastActivity: hoursAgo(0.5),
      trend: { filesProcessed: 850, change: 5, changeType: 'positive' },
    },
    createdBy: {
      userId: 'user-002',
      name: 'Bob Smith',
      email: 'bob@example.com',
    },
    createdAt: daysAgo(180),
    updatedAt: hoursAgo(0.5),
  },
  {
    datasetId: 'ds-003',
    datasetName: 'customer-feedback',
    description: 'Customer survey responses and feedback data',
    teamId: 'team-002',
    teamName: 'Customer Experience',
    status: 'active',
    processingType: 'excel-to-json',
    config: {
      basicSettings: {
        datasetName: 'customer-feedback',
        teamId: 'team-002',
        description: 'Customer survey responses and feedback data',
        processingType: 'excel-to-json',
      },
      schemaConfig: [
        {
          columnName: 'response_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['RESP-001'],
        },
        {
          columnName: 'rating',
          dataType: 'integer',
          nullable: false,
          sampleValues: ['4', '5'],
        },
        {
          columnName: 'comment',
          dataType: 'string',
          nullable: true,
          sampleValues: ['Great service!'],
        },
      ],
      sqlTransform:
        'SELECT response_id, rating, comment, submitted_at FROM source WHERE rating IS NOT NULL',
      processingOptions: {
        concurrencyLimit: 5,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: true,
          checkDuplicates: true,
        },
      },
      andesSettings: {
        provider: 'cx-data-provider',
        tableName: 'customer_feedback',
        tableVersion: 1,
        partitionKeys: ['date'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 320,
      successRate: 95.0,
      avgProcessingTime: 120,
      lastActivity: daysAgo(1),
      trend: { filesProcessed: 32, change: -8, changeType: 'negative' },
    },
    createdBy: {
      userId: 'user-003',
      name: 'Carol Davis',
      email: 'carol@example.com',
    },
    createdAt: daysAgo(60),
    updatedAt: daysAgo(1),
  },
  {
    datasetId: 'ds-004',
    datasetName: 'order-fulfillment',
    description: 'Order fulfillment and shipping status updates',
    teamId: 'team-003',
    teamName: 'Operations',
    status: 'processing',
    processingType: 'csv-to-json',
    config: {
      basicSettings: {
        datasetName: 'order-fulfillment',
        teamId: 'team-003',
        description: 'Order fulfillment and shipping status updates',
        processingType: 'csv-to-json',
      },
      schemaConfig: [
        {
          columnName: 'order_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['ORD-123456'],
        },
        {
          columnName: 'status',
          dataType: 'string',
          nullable: false,
          sampleValues: ['shipped', 'delivered'],
        },
        {
          columnName: 'tracking_number',
          dataType: 'string',
          nullable: true,
          sampleValues: ['1Z999AA10123456784'],
        },
      ],
      sqlTransform:
        'SELECT * FROM source WHERE status IN ("shipped", "delivered", "returned")',
      processingOptions: {
        concurrencyLimit: 20,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: true,
          checkDuplicates: false,
        },
      },
      andesSettings: {
        provider: 'ops-data-provider',
        tableName: 'order_fulfillment',
        tableVersion: 3,
        partitionKeys: ['date', 'status'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 4200,
      successRate: 97.8,
      avgProcessingTime: 35,
      lastActivity: hoursAgo(0.1),
      trend: { filesProcessed: 420, change: 15, changeType: 'positive' },
    },
    createdBy: {
      userId: 'user-004',
      name: 'David Lee',
      email: 'david@example.com',
    },
    createdAt: daysAgo(120),
    updatedAt: hoursAgo(0.1),
  },
  {
    datasetId: 'ds-005',
    datasetName: 'marketing-campaigns',
    description: 'Marketing campaign performance metrics',
    teamId: 'team-004',
    teamName: 'Marketing',
    status: 'draft',
    processingType: 'csv-to-json',
    config: {
      basicSettings: {
        datasetName: 'marketing-campaigns',
        teamId: 'team-004',
        description: 'Marketing campaign performance metrics',
        processingType: 'csv-to-json',
      },
      schemaConfig: [
        {
          columnName: 'campaign_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['CAMP-2025-Q1'],
        },
        {
          columnName: 'impressions',
          dataType: 'integer',
          nullable: false,
          sampleValues: ['150000'],
        },
        {
          columnName: 'clicks',
          dataType: 'integer',
          nullable: false,
          sampleValues: ['3500'],
        },
        {
          columnName: 'conversions',
          dataType: 'integer',
          nullable: false,
          sampleValues: ['120'],
        },
      ],
      sqlTransform:
        'SELECT campaign_id, impressions, clicks, conversions, spend FROM source',
      processingOptions: {
        concurrencyLimit: 5,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: true,
          checkDuplicates: true,
        },
      },
      andesSettings: {
        provider: 'marketing-provider',
        tableName: 'campaign_metrics',
        tableVersion: 1,
        partitionKeys: ['date'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 180,
      successRate: 100,
      avgProcessingTime: 60,
      lastActivity: daysAgo(14),
      trend: { filesProcessed: 0, change: 0, changeType: 'neutral' },
    },
    createdBy: {
      userId: 'user-005',
      name: 'Emma Wilson',
      email: 'emma@example.com',
    },
    createdAt: daysAgo(200),
    updatedAt: daysAgo(14),
  },
  {
    datasetId: 'ds-006',
    datasetName: 'product-catalog',
    description: 'Product catalog updates and pricing changes',
    teamId: 'team-001',
    teamName: 'Retail Analytics',
    status: 'failed',
    processingType: 'json-passthrough',
    config: {
      basicSettings: {
        datasetName: 'product-catalog',
        teamId: 'team-001',
        description: 'Product catalog updates and pricing changes',
        processingType: 'json-passthrough',
      },
      schemaConfig: [
        {
          columnName: 'product_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['PROD-001'],
        },
        {
          columnName: 'name',
          dataType: 'string',
          nullable: false,
          sampleValues: ['Widget Pro'],
        },
        {
          columnName: 'price',
          dataType: 'decimal',
          nullable: false,
          sampleValues: ['29.99'],
        },
      ],
      sqlTransform: 'SELECT * FROM source',
      processingOptions: {
        concurrencyLimit: 10,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: true,
          checkDuplicates: true,
        },
      },
      andesSettings: {
        provider: 'catalog-provider',
        tableName: 'product_catalog',
        tableVersion: 5,
        partitionKeys: ['category'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 890,
      successRate: 85.2,
      avgProcessingTime: 25,
      lastActivity: hoursAgo(6),
      trend: { filesProcessed: 89, change: -25, changeType: 'negative' },
    },
    createdBy: {
      userId: 'user-001',
      name: 'Alice Chen',
      email: 'alice@example.com',
    },
    createdAt: daysAgo(150),
    updatedAt: hoursAgo(6),
  },

  {
    datasetId: 'ds-007',
    datasetName: 'supplier-invoices',
    description: 'Supplier invoice data for accounts payable',
    teamId: 'team-005',
    teamName: 'Finance',
    status: 'active',
    processingType: 'excel-to-json',
    config: {
      basicSettings: {
        datasetName: 'supplier-invoices',
        teamId: 'team-005',
        description: 'Supplier invoice data for accounts payable',
        processingType: 'excel-to-json',
      },
      schemaConfig: [
        {
          columnName: 'invoice_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['INV-2025-001'],
        },
        {
          columnName: 'supplier_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['SUP-123'],
        },
        {
          columnName: 'amount',
          dataType: 'decimal',
          nullable: false,
          sampleValues: ['5000.00'],
        },
        {
          columnName: 'due_date',
          dataType: 'date',
          nullable: false,
          sampleValues: ['2025-02-15'],
        },
      ],
      sqlTransform:
        'SELECT invoice_id, supplier_id, amount, due_date, status FROM source',
      processingOptions: {
        concurrencyLimit: 5,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: true,
          checkDuplicates: true,
        },
      },
      andesSettings: {
        provider: 'finance-provider',
        tableName: 'supplier_invoices',
        tableVersion: 2,
        partitionKeys: ['month'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 560,
      successRate: 99.5,
      avgProcessingTime: 90,
      lastActivity: hoursAgo(4),
      trend: { filesProcessed: 56, change: 8, changeType: 'positive' },
    },
    createdBy: {
      userId: 'user-006',
      name: 'Frank Miller',
      email: 'frank@example.com',
    },
    createdAt: daysAgo(100),
    updatedAt: hoursAgo(4),
  },
  {
    datasetId: 'ds-008',
    datasetName: 'website-analytics',
    description: 'Website traffic and user behavior analytics',
    teamId: 'team-004',
    teamName: 'Marketing',
    status: 'active',
    processingType: 'json-passthrough',
    config: {
      basicSettings: {
        datasetName: 'website-analytics',
        teamId: 'team-004',
        description: 'Website traffic and user behavior analytics',
        processingType: 'json-passthrough',
      },
      schemaConfig: [
        {
          columnName: 'session_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['sess-abc123'],
        },
        {
          columnName: 'page_views',
          dataType: 'integer',
          nullable: false,
          sampleValues: ['5'],
        },
        {
          columnName: 'duration_seconds',
          dataType: 'integer',
          nullable: false,
          sampleValues: ['180'],
        },
      ],
      sqlTransform: 'SELECT * FROM source WHERE page_views > 0',
      processingOptions: {
        concurrencyLimit: 20,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 1.5 },
        validation: {
          checkSchema: true,
          checkSize: false,
          checkDuplicates: false,
        },
      },
      andesSettings: {
        provider: 'analytics-provider',
        tableName: 'website_sessions',
        tableVersion: 1,
        partitionKeys: ['date', 'hour'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 12500,
      successRate: 99.8,
      avgProcessingTime: 8,
      lastActivity: hoursAgo(0.2),
      trend: { filesProcessed: 1250, change: 20, changeType: 'positive' },
    },
    createdBy: {
      userId: 'user-005',
      name: 'Emma Wilson',
      email: 'emma@example.com',
    },
    createdAt: daysAgo(45),
    updatedAt: hoursAgo(0.2),
  },
  {
    datasetId: 'ds-009',
    datasetName: 'employee-timesheets',
    description: 'Employee timesheet and attendance records',
    teamId: 'team-006',
    teamName: 'Human Resources',
    status: 'active',
    processingType: 'csv-to-json',
    config: {
      basicSettings: {
        datasetName: 'employee-timesheets',
        teamId: 'team-006',
        description: 'Employee timesheet and attendance records',
        processingType: 'csv-to-json',
      },
      schemaConfig: [
        {
          columnName: 'employee_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['EMP-001'],
        },
        {
          columnName: 'date',
          dataType: 'date',
          nullable: false,
          sampleValues: ['2025-01-15'],
        },
        {
          columnName: 'hours_worked',
          dataType: 'decimal',
          nullable: false,
          sampleValues: ['8.5'],
        },
      ],
      sqlTransform:
        'SELECT employee_id, date, hours_worked, overtime_hours FROM source',
      processingOptions: {
        concurrencyLimit: 5,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: true,
          checkDuplicates: true,
        },
      },
      andesSettings: {
        provider: 'hr-provider',
        tableName: 'employee_timesheets',
        tableVersion: 1,
        partitionKeys: ['pay_period'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 240,
      successRate: 100,
      avgProcessingTime: 30,
      lastActivity: daysAgo(2),
      trend: { filesProcessed: 24, change: 0, changeType: 'neutral' },
    },
    createdBy: {
      userId: 'user-007',
      name: 'Grace Taylor',
      email: 'grace@example.com',
    },
    createdAt: daysAgo(180),
    updatedAt: daysAgo(2),
  },
  {
    datasetId: 'ds-010',
    datasetName: 'support-tickets',
    description: 'Customer support ticket data and resolution metrics',
    teamId: 'team-002',
    teamName: 'Customer Experience',
    status: 'active',
    processingType: 'json-passthrough',
    config: {
      basicSettings: {
        datasetName: 'support-tickets',
        teamId: 'team-002',
        description: 'Customer support ticket data and resolution metrics',
        processingType: 'json-passthrough',
      },
      schemaConfig: [
        {
          columnName: 'ticket_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['TKT-12345'],
        },
        {
          columnName: 'priority',
          dataType: 'string',
          nullable: false,
          sampleValues: ['high', 'medium'],
        },
        {
          columnName: 'status',
          dataType: 'string',
          nullable: false,
          sampleValues: ['open', 'resolved'],
        },
        {
          columnName: 'resolution_time_hours',
          dataType: 'decimal',
          nullable: true,
          sampleValues: ['2.5'],
        },
      ],
      sqlTransform: 'SELECT * FROM source',
      processingOptions: {
        concurrencyLimit: 10,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: true,
          checkDuplicates: false,
        },
      },
      andesSettings: {
        provider: 'cx-data-provider',
        tableName: 'support_tickets',
        tableVersion: 2,
        partitionKeys: ['date', 'priority'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 1800,
      successRate: 97.5,
      avgProcessingTime: 20,
      lastActivity: hoursAgo(1),
      trend: { filesProcessed: 180, change: 10, changeType: 'positive' },
    },
    createdBy: {
      userId: 'user-003',
      name: 'Carol Davis',
      email: 'carol@example.com',
    },
    createdAt: daysAgo(90),
    updatedAt: hoursAgo(1),
  },
  {
    datasetId: 'ds-011',
    datasetName: 'warehouse-shipments',
    description: 'Warehouse outbound shipment tracking data',
    teamId: 'team-003',
    teamName: 'Operations',
    status: 'active',
    processingType: 'csv-to-json',
    config: {
      basicSettings: {
        datasetName: 'warehouse-shipments',
        teamId: 'team-003',
        description: 'Warehouse outbound shipment tracking data',
        processingType: 'csv-to-json',
      },
      schemaConfig: [
        {
          columnName: 'shipment_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['SHIP-001'],
        },
        {
          columnName: 'warehouse_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['WH-SEA'],
        },
        {
          columnName: 'carrier',
          dataType: 'string',
          nullable: false,
          sampleValues: ['UPS', 'FedEx'],
        },
        {
          columnName: 'weight_kg',
          dataType: 'decimal',
          nullable: false,
          sampleValues: ['2.5'],
        },
      ],
      sqlTransform:
        'SELECT shipment_id, warehouse_id, carrier, weight_kg, shipped_at FROM source',
      processingOptions: {
        concurrencyLimit: 15,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: true,
          checkDuplicates: true,
        },
      },
      andesSettings: {
        provider: 'ops-data-provider',
        tableName: 'warehouse_shipments',
        tableVersion: 1,
        partitionKeys: ['date', 'warehouse_id'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 3200,
      successRate: 98.9,
      avgProcessingTime: 28,
      lastActivity: hoursAgo(0.5),
      trend: { filesProcessed: 320, change: 12, changeType: 'positive' },
    },
    createdBy: {
      userId: 'user-004',
      name: 'David Lee',
      email: 'david@example.com',
    },
    createdAt: daysAgo(75),
    updatedAt: hoursAgo(0.5),
  },
  {
    datasetId: 'ds-012',
    datasetName: 'vendor-payments',
    description: 'Vendor payment processing and reconciliation',
    teamId: 'team-005',
    teamName: 'Finance',
    status: 'active',
    processingType: 'excel-to-json',
    config: {
      basicSettings: {
        datasetName: 'vendor-payments',
        teamId: 'team-005',
        description: 'Vendor payment processing and reconciliation',
        processingType: 'excel-to-json',
      },
      schemaConfig: [
        {
          columnName: 'payment_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['PAY-2025-001'],
        },
        {
          columnName: 'vendor_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['VND-123'],
        },
        {
          columnName: 'amount',
          dataType: 'decimal',
          nullable: false,
          sampleValues: ['10000.00'],
        },
        {
          columnName: 'payment_date',
          dataType: 'date',
          nullable: false,
          sampleValues: ['2025-01-20'],
        },
      ],
      sqlTransform:
        'SELECT payment_id, vendor_id, amount, payment_date, status FROM source',
      processingOptions: {
        concurrencyLimit: 5,
        retryPolicy: { maxAttempts: 5, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: true,
          checkDuplicates: true,
        },
      },
      andesSettings: {
        provider: 'finance-provider',
        tableName: 'vendor_payments',
        tableVersion: 1,
        partitionKeys: ['month'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 420,
      successRate: 99.8,
      avgProcessingTime: 75,
      lastActivity: hoursAgo(8),
      trend: { filesProcessed: 42, change: 5, changeType: 'positive' },
    },
    createdBy: {
      userId: 'user-006',
      name: 'Frank Miller',
      email: 'frank@example.com',
    },
    createdAt: daysAgo(60),
    updatedAt: hoursAgo(8),
  },
  {
    datasetId: 'ds-013',
    datasetName: 'quality-inspections',
    description: 'Product quality inspection results',
    teamId: 'team-003',
    teamName: 'Operations',
    status: 'active',
    processingType: 'csv-to-json',
    config: {
      basicSettings: {
        datasetName: 'quality-inspections',
        teamId: 'team-003',
        description: 'Product quality inspection results',
        processingType: 'csv-to-json',
      },
      schemaConfig: [
        {
          columnName: 'inspection_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['QI-001'],
        },
        {
          columnName: 'product_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['PROD-123'],
        },
        {
          columnName: 'result',
          dataType: 'string',
          nullable: false,
          sampleValues: ['pass', 'fail'],
        },
        {
          columnName: 'defect_count',
          dataType: 'integer',
          nullable: false,
          sampleValues: ['0', '2'],
        },
      ],
      sqlTransform: 'SELECT * FROM source',
      processingOptions: {
        concurrencyLimit: 10,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: true,
          checkDuplicates: true,
        },
      },
      andesSettings: {
        provider: 'ops-data-provider',
        tableName: 'quality_inspections',
        tableVersion: 1,
        partitionKeys: ['date'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 680,
      successRate: 99.1,
      avgProcessingTime: 22,
      lastActivity: hoursAgo(3),
      trend: { filesProcessed: 68, change: 3, changeType: 'positive' },
    },
    createdBy: {
      userId: 'user-008',
      name: 'Henry Brown',
      email: 'henry@example.com',
    },
    createdAt: daysAgo(45),
    updatedAt: hoursAgo(3),
  },
  {
    datasetId: 'ds-014',
    datasetName: 'social-media-metrics',
    description: 'Social media engagement and reach metrics',
    teamId: 'team-004',
    teamName: 'Marketing',
    status: 'draft',
    processingType: 'json-passthrough',
    config: {
      basicSettings: {
        datasetName: 'social-media-metrics',
        teamId: 'team-004',
        description: 'Social media engagement and reach metrics',
        processingType: 'json-passthrough',
      },
      schemaConfig: [
        {
          columnName: 'post_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['POST-001'],
        },
        {
          columnName: 'platform',
          dataType: 'string',
          nullable: false,
          sampleValues: ['twitter', 'instagram'],
        },
        {
          columnName: 'likes',
          dataType: 'integer',
          nullable: false,
          sampleValues: ['1500'],
        },
        {
          columnName: 'shares',
          dataType: 'integer',
          nullable: false,
          sampleValues: ['250'],
        },
      ],
      sqlTransform: 'SELECT * FROM source',
      processingOptions: {
        concurrencyLimit: 10,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: false,
          checkDuplicates: false,
        },
      },
      andesSettings: {
        provider: 'marketing-provider',
        tableName: 'social_media_metrics',
        tableVersion: 1,
        partitionKeys: ['date', 'platform'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 150,
      successRate: 98.0,
      avgProcessingTime: 15,
      lastActivity: daysAgo(30),
      trend: { filesProcessed: 0, change: 0, changeType: 'neutral' },
    },
    createdBy: {
      userId: 'user-005',
      name: 'Emma Wilson',
      email: 'emma@example.com',
    },
    createdAt: daysAgo(120),
    updatedAt: daysAgo(30),
  },
  {
    datasetId: 'ds-015',
    datasetName: 'returns-processing',
    description: 'Product returns and refund processing data',
    teamId: 'team-002',
    teamName: 'Customer Experience',
    status: 'active',
    processingType: 'csv-to-json',
    config: {
      basicSettings: {
        datasetName: 'returns-processing',
        teamId: 'team-002',
        description: 'Product returns and refund processing data',
        processingType: 'csv-to-json',
      },
      schemaConfig: [
        {
          columnName: 'return_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['RET-001'],
        },
        {
          columnName: 'order_id',
          dataType: 'string',
          nullable: false,
          sampleValues: ['ORD-123'],
        },
        {
          columnName: 'reason',
          dataType: 'string',
          nullable: false,
          sampleValues: ['defective', 'wrong_item'],
        },
        {
          columnName: 'refund_amount',
          dataType: 'decimal',
          nullable: false,
          sampleValues: ['49.99'],
        },
      ],
      sqlTransform:
        'SELECT return_id, order_id, reason, refund_amount, processed_at FROM source',
      processingOptions: {
        concurrencyLimit: 10,
        retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
        validation: {
          checkSchema: true,
          checkSize: true,
          checkDuplicates: true,
        },
      },
      andesSettings: {
        provider: 'cx-data-provider',
        tableName: 'returns_processing',
        tableVersion: 1,
        partitionKeys: ['date'],
      },
      outputConfig: { track: 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
    },
    stats: {
      totalFilesProcessed: 920,
      successRate: 96.5,
      avgProcessingTime: 35,
      lastActivity: hoursAgo(2),
      trend: { filesProcessed: 92, change: 8, changeType: 'positive' },
    },
    createdBy: {
      userId: 'user-003',
      name: 'Carol Davis',
      email: 'carol@example.com',
    },
    createdAt: daysAgo(55),
    updatedAt: hoursAgo(2),
  },
];

// Export helper functions
export const getDatasetById = (id: string): Dataset | undefined => {
  return mockDatasets.find((d) => d.datasetId === id);
};

export const getDatasetsByTeam = (teamId: string): Dataset[] => {
  return mockDatasets.filter((d) => d.teamId === teamId);
};

export const getDatasetsByStatus = (status: DatasetStatus): Dataset[] => {
  return mockDatasets.filter((d) => d.status === status);
};

export const getDatasetsByProcessingType = (
  type: ProcessingType,
): Dataset[] => {
  return mockDatasets.filter((d) => d.processingType === type);
};
